import React, { useEffect, useRef } from 'react';

import {
  useAppDispatch,
  useAppSelector,
  useTranslate,
  useWidgetsTheme as theme,
  useWidgetParam,
} from '@src/core/hooks';
import {
  completeCartSelector,
  cartSetCouponErrorsSelector,
  openCartModal,
  cartStateSelector,
} from '@src/features/cart/store/cart';
import { confirmCartOrder } from '@src/features/orders/store/order';
import {
  CartConfirmationOutcome,
  resolveCartConfirmationOutcome,
} from '@src/features/orders/core/resolveCartConfirmationOutcome';
import { DefaultMessagesKeys } from '@src/i18n/useTranslate';
import styled from 'styled-components';
import { LayoutColors } from '@src/core/theme';
import { Spinner } from '@src/components/reusable';
import { changeLocation } from '@src/core/store';
import { checkoutErrorsSelector } from '@src/stores/checkout';

export const SaleConfirmation = () => {
  const dispatch = useAppDispatch();
  const { isLoading, isReady, hasErrors } = useAppSelector(completeCartSelector);
  const couponErrors = useAppSelector(cartSetCouponErrorsSelector);
  const checkoutErrors = useAppSelector(checkoutErrorsSelector);
  const { i18nElement, i18n } = useTranslate();
  const actionColor = theme('actionColor');
  const { isModalOpened } = useAppSelector(cartStateSelector);
  const isFirstRender = useRef(false);

  const accountPage = useWidgetParam('account-page');
  const redirect = () => dispatch(changeLocation(accountPage ?? ''));

  useEffect(() => {
    if (!isLoading && !isFirstRender.current) {
      isFirstRender.current = true;

      const outcome = resolveCartConfirmationOutcome({
        isLoading,
        isReady,
        hasErrors,
        isModalOpened,
        hasCouponErrors: !!couponErrors,
      });

      if (outcome === CartConfirmationOutcome.OpenCartModal) {
        dispatch(openCartModal());
      } else if (outcome === CartConfirmationOutcome.Confirm) {
        dispatch(confirmCartOrder());
      } else if (outcome === CartConfirmationOutcome.Redirect) {
        redirect();
      }
    }
  }, [isLoading, isReady, couponErrors, hasErrors]);

  if (isLoading) {
    return (
      <SpinnerWrapper>
        <Spinner visible spinnerColor={actionColor} />
      </SpinnerWrapper>
    );
  }

  if (couponErrors) {
    return (
      <ErrorMessage>
        {couponErrors.map((key) => (
          <span key={key}>{i18n(key as DefaultMessagesKeys)}</span>
        ))}
      </ErrorMessage>
    );
  }

  if (checkoutErrors?.length) {
    return (
      <ErrorMessage>
        {checkoutErrors.map((key) => (
          <span key={key}>{i18n(key as DefaultMessagesKeys)}</span>
        ))}
      </ErrorMessage>
    );
  }

  if (!isLoading && !isReady) {
    return (
      <SpinnerWrapper>
        <Spinner visible spinnerColor={actionColor} />
      </SpinnerWrapper>
    );
  }

  return isReady ? (
    <div>{i18nElement('checkout_confirmingYourPurchase')}</div>
  ) : (
    <div>{i18nElement('checkout_yourCartIsEmpty')}</div>
  );
};

const ErrorMessage = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${LayoutColors.errorDark} !important;

  span {
    text-align: center;
  }
`;

const SpinnerWrapper = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto;
`;

export default SaleConfirmation;
